import type {
    Stages,
    Template,
} from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import { Card, CardHeader, RadioGroup, Heading } from "@amzn/storm-ui";
import { useAsync, useDebouncedEffect } from "@react-hookz/web";
import { isEmpty } from "lodash";
import React from "react";
import { useStore } from "zustand";

import { getPublisherPortalApiClient } from "../../../api/publisherPortalApi/publisherPortalApi";
import { useAsyncErrorStatusNotification } from "../../../hooks/notifications/useAsyncErrorStatusNotification";
import { authStore } from "../../../store/auth";
import { dimensionStatusStore } from "../../../store/dimension.status";
import { dimensionValueStore } from "../../../store/dimension.value";
import { getStateKeys } from "../../../store/zustand.helpers";
import { isLoading } from "../../../utils/api.utils";
import { sortTemplates } from "../../../utils/createInsight.utils";
import { LoadingSpinner } from "../../LoadingSpinner";

import { TemplateSelector } from "./TemplateSelector";

export interface TemplatesCardProps {
    domainIdentifier: string;
    templateIdentifier: string;
    setTemplateIdentifier: (templateIdentifier: string) => void;
}

export const TemplatesCard: React.FunctionComponent<TemplatesCardProps> = ({
    domainIdentifier,
    templateIdentifier,
    setTemplateIdentifier,
}) => {
    const [dimensionStatusSuccess] = useStore(dimensionStatusStore, (state) => [
        state.checkAllStatusSuccess(),
    ]);

    const [dimensionsIds] = useStore(dimensionValueStore, (state) => [
        getStateKeys(state),
    ]);

    const [dimensionData] = useStore(dimensionValueStore, (state) => [
        state.getDimensionValueFilters(dimensionsIds),
    ]);

    const [stage] = useStore(authStore, (state) => [state.settings.stage]);

    const [templatesState, templatesAction] = useAsync(
        async (): Promise<Template[]> => {
            const { data: templates = [] } = await (
                await getPublisherPortalApiClient()
            ).getTemplates({
                domainIdentifier,
                stage: stage as Stages,
            });

            // language filter client side check
            const languageFilter = dimensionData.find(
                (e) => e.key === "language",
            );

            if (languageFilter) {
                return templates.filter(
                    (t) =>
                        t.language?.toLowerCase() ===
                        languageFilter.value?.toLowerCase(),
                );
            }
            return templates;
        },
    );
    useAsyncErrorStatusNotification(templatesState);

    const dimensionSelectionComplete = React.useMemo(() => {
        return !isEmpty(dimensionData) && dimensionStatusSuccess;
    }, [dimensionData, dimensionStatusSuccess]);

    useDebouncedEffect(
        () => {
            setTemplateIdentifier("");
            if (dimensionSelectionComplete) {
                void templatesAction.execute();
            }
        },
        [dimensionSelectionComplete, JSON.stringify(dimensionData)],
        200,
    );

    const templates = React.useMemo(() => {
        return dimensionSelectionComplete ? templatesState.result : [];
    }, [templatesState.result, dimensionSelectionComplete]);

    const handleChange = (e) => {
        setTemplateIdentifier(e.target.value);
    };

    return (
        <Card>
            <CardHeader>
                <Heading renderAs="h2">Decks</Heading>
            </CardHeader>
            {!dimensionSelectionComplete && (
                <Heading renderAs="h4">
                    Please complete the settings card to see decks
                </Heading>
            )}
            {isLoading(templatesState.status) ? (
                <LoadingSpinner />
            ) : (
                <RadioGroup
                    aria-labelledby="select-templates"
                    name="select-templates"
                >
                    {sortTemplates(templates || []).map(
                        (template: Template) => {
                            return (
                                <TemplateSelector
                                    key={template.id}
                                    template={template}
                                    templateIdentifier={templateIdentifier}
                                    handleChange={handleChange}
                                    disabled={
                                        template.enabled !== undefined
                                            ? !template.enabled
                                            : false
                                    }
                                />
                            );
                        },
                    )}
                </RadioGroup>
            )}
        </Card>
    );
};
